/**
 * THEME JSON CONFIG CSS VARS
 * The following default component scss variables can be overwritten via theme configuration per brand.
 * If you need to add new one, please make sure to set a default value.
 * JSON Definition for Component with name "Example": testCssVar: "20px"
 * Resulting CSS var: $example-test-css-var: 20px;
 */

$page-header-default-shadow: none !default;
$page-header-indicator-border-radius: 14px !default;
$page-header-indicator-height: 48px !default;
$page-header-indicator-width: 48px !default;
$page-header-indicator-shadow: 0px 0px 24px rgba(0, 0, 0, 0.1) !default;
$page-header-indicator-shadow-hover: 0px 0px 24px rgba(0, 0, 0, 0.2) !default;
$page-header-indicator-color-light: #ffffff !default;
$page-header-indicator-color-dark: #000000 !default;

/* END THEME JSON CONFIG CSS VARS ******************************************************* */

:host {
  display: block;
  font-family: $font-family-base;

  // Estimation of height of header of the page. This value will be overwritten if a real calculated
  // height is passed down as attribute to the web component.
  // --content-overlapping-elements-height: 120px;
  --content-overlapping-elements-height: 0px;
}

.#{$prefix}interaction-wrapper {
  position: absolute;
  width: 100%;
  height: auto;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 32px;
  z-index: 2;
  padding: 0 48px;
  box-sizing: border-box;

  &.#{$prefix}top {
    top: 48px;

    @include for-size(desktop) {
      top: 40px;
    }

    @include for-size(tablet) {
      top: 32px;
    }

    @include for-size(phone) {
      top: 24px;
    }

    &.#{$prefix}relative {
      position: relative;
      top: unset;
      padding-bottom: 48px;
    }
  }

  &.#{$prefix}bottom {
    bottom: 48px;

    @include for-size(desktop) {
      bottom: 40px;
    }

    @include for-size(tablet) {
      bottom: 32px;
    }

    @include for-size(phone) {
      bottom: 24px;
    }

    &.#{$prefix}relative {
      position: relative;
      bottom: unset;
      padding-top: 48px;
    }
  }

  @include for-size(desktop) {
    padding: 0 40px;
  }

  @include for-size(tablet) {
    padding: 0 32px;
  }

  @include for-size(phone) {
    padding: 0 24px;
  }

  .#{$prefix}full-height-indicator-wrapper {
    position: relative;
    height: 48px;
    cursor: pointer;
    transition: opacity 0.2s ease-in-out;
    opacity: 0.6;
    border-radius: $page-header-indicator-border-radius;

    &:hover {
      opacity: 1;

      .#{$prefix}indicator-svg {
        filter: drop-shadow($page-header-indicator-shadow-hover);
      }
    }

    &.#{$prefix}indicator-light {
      &:focus-visible {
        @include focusBorder($page-header-indicator-color-light);
      }

      .#{$prefix}indicator-path {
        fill: $page-header-indicator-color-light;
      }
    }

    &.#{$prefix}indicator-dark {
      &:focus-visible {
        @include focusBorder($page-header-indicator-color-dark);
      }

      .#{$prefix}indicator-path {
        fill: $page-header-indicator-color-dark;
      }
    }

    .#{$prefix}indicator-svg {
      height: $page-header-indicator-height;
      width: $page-header-indicator-width;
      filter: drop-shadow($page-header-indicator-shadow);
    }
  }
}

.#{$prefix}page-header-violator {
  position: absolute;
  box-sizing: border-box;
  top: 48px;
  left: 48px;

  @include for-size(desktop) {
    top: 40px;
    left: 40px;
  }

  @include for-size(tablet) {
    top: 32px;
    left: 32px;
  }

  @include for-size(phone) {
    top: 24px;
    left: 24px;
  }

  &.#{$prefix}position-x-right {
    left: auto;
    right: 48px;

    @include for-size(desktop) {
      right: 40px;
    }
    @include for-size(tablet) {
      right: 32px;
    }
    @include for-size(phone) {
      right: 24px;
    }
  }

  &.#{$prefix}position-y-bottom {
    top: auto;
    bottom: 48px;

    @include for-size(desktop) {
      bottom: 40px;
    }
    @include for-size(tablet) {
      bottom: 32px;
    }
    @include for-size(phone) {
      bottom: 24px;
    }
  }

  &.#{$prefix}violator-on-image {
    position: relative;
    box-sizing: border-box;
    margin-bottom: 80px;
    align-self: flex-start;
    justify-self: flex-start;
    flex: 1;
    top: 0;
    left: 0;

    @include for-size(desktop) {
      margin-bottom: 40px;
    }

    @include for-size(tablet) {
      margin-bottom: 32px;
    }

    @include for-size(phone) {
      position: relative;
      margin-bottom: 24px;
    }

    &.#{$prefix}position-x-right {
      align-self: flex-end;
    }

    &.#{$prefix}position-y-bottom {
      display: flex;
      align-items: flex-end;
      order: 1;
      margin-bottom: 0;
      margin-top: 80px;

      @include for-size(desktop) {
        margin-top: 40px;
      }

      @include for-size(tablet) {
        margin-top: 32px;
      }

      @include for-size(phone) {
        margin-top: 24px;
      }
    }
  }
}

@import "./page-header-variant-horizontal";
@import "./page-header-variant-vertical";
@import "./page-header-variant-image";
@import "page-header-variant-offset-content";

::slotted([slot="header-content"]) {
  max-width: 100%;
}

::slotted(eon-ui-link) {
  --contentHoverColor: var(--linkHoverColor) !important;
}
